import type { Breakpoint } from './theme/breakpoints';
import { BREAKPOINT_ORDER } from './theme/breakpoints';

/**
 * Accept either a single string prop='value' or an object keyed by breakpoints prop={base: 'value', md: 'value'}.
 *
 */
export type ResponsiveProp<T> = T | Partial<Record<Breakpoint, T>>;

/**
 * Upgrade only the keys in K to be responsive.
 */
export type WithResponsive<T, K extends keyof T> = Omit<T, K> & {
  [P in K]?: ResponsiveProp<T[P]>;
};

/**
 * Resolve a responsive prop to a single value based on the current breakpoint.
 * This is a mobile-first approach, meaning that the base value is used for
 * smaller breakpoints, and larger values are used for larger breakpoints.
 */
export function resolveResponsiveProp<T>(
  value: ResponsiveProp<T>,
  current: Breakpoint,
): T {
  /**
   * If the value is not an object, we can return it directly.
   * This is the case for scalar values like strings or numbers.
   */
  if (typeof value !== 'object' || value === null) {
    return value as T;
  }

  /**
   * The value is an object, so we need to check for the current breakpoint.
   * We use a type assertion to tell TypeScript that the value is
   * a Partial<Record<Breakpoint, T>>. This allows us to access the keys
   * of the object without TypeScript complaining.
   */
  const typedValue = value as Partial<Record<Breakpoint, T>>;

  /**
   * If the current breakpoint is defined in the object, we can return it.
   * This is the case for breakpoints like "sm", "md", "lg", etc.
   */
  if (typedValue[current] !== undefined) {
    return typedValue[current] as T;
  }

  /**
   * If the current breakpoint is not defined, we need to walk down the
   * mobile-first order until we find a defined key. We start from the
   * current breakpoint and go down to the base breakpoint.
   */
  const startIndex = BREAKPOINT_ORDER.indexOf(current);
  for (let i = startIndex - 1; i >= 0; i--) {
    const lowerKey = BREAKPOINT_ORDER[i];
    if (typedValue[lowerKey] !== undefined) {
      return typedValue[lowerKey] as T;
    }
  }

  /**
   * If no defined key is found, we return the first value in the object.
   */
  return Object.values(typedValue)[0] as T;
}
